import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class BundleOptimizationService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  private loadedChunks = new Set<string>();

  /**
   * Preload critical chunks for faster navigation
   */
  preloadCriticalChunks(): void {
    if (!this.isBrowser) return;

    const criticalChunks = [
      'chunk-KWTCEPL7.js', // Largest chunk (59.10 KiB)
      'chunk-HNFZE6B4.js', // 17.73 KiB
      'chunk-XNN26TAS.js'  // 21.26 KiB
    ];

    criticalChunks.forEach(chunk => {
      this.preloadChunk(chunk);
    });
  }

  /**
   * Preload chunk with high priority
   */
  private preloadChunk(chunkName: string): void {
    if (this.loadedChunks.has(chunkName)) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = `/${chunkName}`;
    link.fetchPriority = 'high';

    document.head.appendChild(link);
    this.loadedChunks.add(chunkName);
  }

  /**
   * Optimize chunk loading order based on critical path
   */
  optimizeChunkLoading(): void {
    if (!this.isBrowser) return;

    // Define loading priority based on critical path analysis
    const chunkPriority = [
      'main-HBCS4H6W.js',      // Main bundle
      'polyfills-TECF5RVS.js', // Polyfills
      'chunk-KWTCEPL7.js',     // Largest chunk
      'chunk-HNFZE6B4.js',     // High priority
      'chunk-XNN26TAS.js',     // High priority
      'chunk-DGDTW3AR.js',     // 19.36 KiB
      'chunk-7VCD6N4H.js',     // 7.39 KiB
      'chunk-4VQF33QX.js',     // 6.07 KiB
      'chunk-7L2OCZN4.js',     // 4.63 KiB
      'chunk-YAVEEE2D.js',     // 3.03 KiB
      'chunk-BPX2CZK2.js',     // 3.89 KiB
      'chunk-36VF6GFX.js',     // 2.48 KiB
      'chunk-52I2URZO.js',     // 1.63 KiB
      'chunk-YTVP3WVS.js',     // 0.92 KiB
      'chunk-YDOXD4O3.js',     // 0.96 KiB
      'chunk-ZH4VWP6J.js',     // 0.76 KiB
      'chunk-UKWEZ36Y.js',     // 0.75 KiB
      'chunk-FEYCXSUF.js',     // 0.84 KiB
      'chunk-KA7NT5B7.js',     // 0.38 KiB
      'chunk-PNLC47W4.js',     // 0.37 KiB
      'chunk-6UQSIYGB.js',     // 2.50 KiB
      'chunk-7OV7GDYS.js',     // 0.51 KiB
      'chunk-2WJNAX5C.js',     // 3.92 KiB
      'chunk-27FUMEZN.js',     // 0.59 KiB
      'chunk-JLBFDQJV.js',     // 2.51 KiB
      'chunk-5QIQVNSY.js',     // 1.21 KiB
      'chunk-AJ7MORLN.js',     // 3.05 KiB
      'chunk-DGIQCWSQ.js',     // 2.88 KiB
      'chunk-L73JX4LQ.js',     // 4.33 KiB
      'chunk-RD3IEEZX.js',     // 0.61 KiB
      'chunk-NYMTYX6N.js',     // 0.55 KiB
      'chunk-4NLLDFVQ.js',     // 1.52 KiB
      'chunk-EIEMWP5M.js',     // 0.71 KiB
      'chunk-MU34TSQE.js',     // 10.93 KiB
      'chunk-KSJYOVRR.js',     // 2.79 KiB
      'chunk-5PE3JID5.js',     // 0.41 KiB
      'chunk-NEPC64T7.js'      // 3.17 KiB
    ];

    // Preload high priority chunks
    chunkPriority.slice(0, 5).forEach(chunk => {
      this.preloadChunk(chunk);
    });

    // Load remaining chunks with lower priority
    chunkPriority.slice(5).forEach((chunk, index) => {
      setTimeout(() => {
        this.preloadChunk(chunk);
      }, (index + 1) * 100); // Stagger loading
    });
  }

  /**
   * Monitor chunk loading performance
   */
  monitorChunkPerformance(): void {
    if (!this.isBrowser) return;

    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'resource' && entry.name.includes('chunk')) {

          // Track slow chunks
          if (entry.duration > 2000) {
          }
        }
      });
    });

    observer.observe({ entryTypes: ['resource'] });
  }

  /**
   * Optimize font loading
   */
  optimizeFontLoading(): void {
    if (!this.isBrowser) return;

    // Preload critical fonts
    const criticalFonts = [
      '/assets/fonts/bwgradualdemo-regular-webfont.woff2',
      '/assets/fonts/bwgradualdemo-bold-webfont.woff2'
    ];

    criticalFonts.forEach(font => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'font';
      link.href = font;
      link.crossOrigin = 'anonymous';
      link.type = 'font/woff2';

      document.head.appendChild(link);
    });

    // Add font-display: swap to CSS
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'bwgradualdemo';
        font-display: swap;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Get bundle size recommendations
   */
  getBundleSizeRecommendations(): { warning: string; recommendations: string[] } {
    return {
      warning: 'Current critical path latency: 5,138ms (target: <2,000ms)',
      recommendations: [
        'Reduce initial bundle size from 1MB to <800KB',
        'Implement route-based code splitting',
        'Use dynamic imports for non-critical features',
        'Optimize third-party library imports',
        'Implement tree shaking for unused code',
        'Use webpack bundle analyzer to identify large dependencies',
        'Consider using module federation for micro-frontends',
        'Implement service worker caching for static assets'
      ]
    };
  }
}
